import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { REGION_SERVER_IDENTIFIER } from "@constants/server-constants";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { RegisterableServer } from "@custom-types/server-types";
import { useToast } from "@flat-ui";
import { runtimeConfig } from "@src/runtime-config";
import {
  isRegisterableServer,
  isServerWithPreassignedStatus,
  isServerWithUnreachableStatus,
} from "@utils/servers-utils";
import { useCallback, useState } from "react";

/** Generic error title to display when the email validation fails */
const EMAIL_VALIDATION_ERROR_TITLE =
  "Something went wrong while validating your email address.";

/**
 * Generic error message to display when the email validation fails.
 * The message is generic to avoid disclosing whether the email is already
 * linked to an existing account.
 */
const EMAIL_VALIDATION_ERROR_MESSAGE =
  "Please try again in a moment or verify that the email address is correct. The email address might already be linked to an existing account.";

/** Generic error title to display when the email validation fails for preassigned servers with GENERIC_OAUTH login provider */
const PREASSIGNED_WITH_GENERIC_OAUTH_ERROR_TITLE =
  "Registration with the provided email address is not possible.";

/** Generic error message to display when the email validation fails for preassigned servers with GENERIC_OAUTH login provider */
const PREASSIGNED_WITH_GENERIC_OAUTH_ERROR_MESSAGE =
  "Please contact support to register an account with this email address.";

interface UseGetRegisterableServer {
  /** Function to fetch a registerable server based on the provided email */
  getRegisterableServer: (
    email: string
  ) => Promise<RegisterableServer | undefined>;

  /** Whether the server fetching process is ongoing */
  isFetchingServer: boolean;
}

/**
 * Custom hook to fetch and return a registerable server entity
 * based on the provided email address.
 */
export function useGetRegisterableServer(): UseGetRegisterableServer {
  const { handleError } = useErrorHandlerContext();
  const { closeToast, openToast } = useToast();
  const [isFetchingServer, setIsFetchingServer] = useState<boolean>(false);

  /**
   * Fetches the server entity associated with the provided email address.
   * It then verifies that the server entity is eligible for signup.
   */
  const getRegisterableServer = useCallback(
    async (email: string): Promise<RegisterableServer | undefined> => {
      setIsFetchingServer(true);

      try {
        const { servers } =
          await faroCoreApiClient.users.getUserServersByEmail(email);
        const server = servers.find((s) => {
          return (
            s.identifier === REGION_SERVER_IDENTIFIER[runtimeConfig.appRegion]
          );
        });

        // Throw an error if the server entity is not found in the backend response or
        // if the server entity has an unreachable status.
        if (!server || isServerWithUnreachableStatus(server)) {
          throw new Error(EMAIL_VALIDATION_ERROR_MESSAGE);
        }

        // Preassigned server entities with GENERIC_OAUTH login provider is a special case
        // that can't be handled by the ULM signup flow. For this case show a warning toast
        // with a generic error message informing the user to contact support.
        if (
          isServerWithPreassignedStatus(server) &&
          server.loginProvider.identifier === "GENERIC_OAUTH"
        ) {
          openToast({
            title: PREASSIGNED_WITH_GENERIC_OAUTH_ERROR_TITLE,
            message: PREASSIGNED_WITH_GENERIC_OAUTH_ERROR_MESSAGE,
            variant: "warning",
          });
        } else if (!isRegisterableServer(server)) {
          // If the server entity is not eligible for registration (i.e. has user_found status),
          // Don't throw an error but do show a toast in error style.
          openToast({
            title: EMAIL_VALIDATION_ERROR_TITLE,
            message: EMAIL_VALIDATION_ERROR_MESSAGE,
            variant: "error",
          });
        } else {
          closeToast();
          return server;
        }
      } catch (error) {
        handleError({
          id: `getRegisterableServer-${Date.now().toString()}`,
          title: EMAIL_VALIDATION_ERROR_TITLE,
          error,
          uiType: "toast",
        });
      } finally {
        setIsFetchingServer(false);
      }
    },
    [closeToast, handleError, openToast]
  );

  return { getRegisterableServer, isFetchingServer };
}
